import React, { useEffect, Fragment, useState, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { IMAGE_URL } from "../../../../utils/api-config";
import { GetBillingCharges } from "../../../../api/masters/billingChargesAPI";
import { GetIpdEntries } from "../../../../api/ipd/ipdEntryAPI";
import { GetIpdBillChargeById, UpdateIpdBillCharge } from "../../../../api/ipd/ipdBillChargesAPI";
import { GetConsultants } from "../../../../api/masters/consultantAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

// Validation schema using Yup
const schema = yup.object().shape({
	ipdEntryId: yup.string().required("IPD Entry is required"),
	billingCharges: yup.array().of(
		yup.object().shape({
			date: yup.date().required("Date is required"),
			billingChargesId: yup.string().required("Billing Charge is required"),
			doctorId: yup.string().required("Doctor Name is required"),
			unit: yup.number().required("Unit is required"),
			rate: yup.number().required("Rate is required"),
			amount: yup.number().required("Amount is required"),
			discount: yup.string().nullable(),
			vNo: yup.string().nullable(),
		})
	),
	totalAmount: yup
		.number()
		.typeError("total Amount must be a number")
		.required("total Amount is required"),
	netAmount: yup
		.number()
		.typeError("Net Amount must be a number")
		.required("Net Amount is required")
})

const EditIpdBillChargesEntry = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { id } = useParams();
	const [opdEntries, setOpdEntries] = useState([]);
	const [ipdEntries, setIpdEntries] = useState([]);
	const [billingCharges, setBillingCharges] = useState([]);
	const [patientDetails, setPatientDetails] = useState({});
	const [doctors, setDoctors] = useState([]);

	const {
		register,
		handleSubmit,
		setValue,
		getValues,
		control,
		watch,
		formState: { errors } } = useForm({
			resolver: yupResolver(schema),
			defaultValues: {
				billingCharges: [
					{
						date: new Date(),
						billingChargesId: "",
						doctorId: "",
						unit: 1,
						rate: 0,
						amount: 0,
						refundAmount: 0,
					}
				]
			}
		});

	const {
		fields: billingChargesFields,
		append: appendBillingCharges,
		remove: removeBillingCharges,
		replace: replaceBillingCharges   // ✅ add this
	} = useFieldArray({
		control,
		name: "billingCharges"
	});

	const fetchIpdEntries = async () => {
		try {
			const response = await GetIpdEntries();
			if (response.success) {
				setIpdEntries(response.data);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	const fetchBillingCharges = async () => {
		try {
			const response = await GetBillingCharges();
			if (response.success) {
				setBillingCharges(response.data);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	const fetchDoctors = async () => {
		try {
			const response = await GetConsultants();
			if (response.success) {
				setDoctors(response.data);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	const calculateAll = () => {
		const rows = getValues("billingCharges");

		// 1. Row amounts
		rows.forEach((row, index) => {
			const unit = parseFloat(row?.unit) || 0;
			const rate = parseFloat(row?.rate) || 0;
			const amount = unit * rate;

			if (row?.amount !== amount) {
				setValue(`billingCharges.${index}.amount`, amount, { shouldDirty: false });
			}
		});

		// 2. total Amount
		const total = rows.reduce(
			(sum, row) =>
				sum + (parseFloat(row?.amount) || 0) - (parseFloat(row?.refundAmount) || 0),
			0
		);

		if (getValues("totalAmount") !== total) {
			setValue("totalAmount", total, { shouldDirty: false });
		}

		const totalAfterRef = total - (parseFloat(getValues("toReference")) || 0);
		// 3. Discount
		const discountPercent = parseFloat(getValues("discountPercentage")) || 0;

		if (discountPercent > 0) {
			const discountAmount = (totalAfterRef * discountPercent) / 100;

			if (getValues("discountAmount") !== discountAmount) {
				setValue("discountAmount", discountAmount.toFixed(2), { shouldDirty: false });
			}
		}

		const finalDiscount = parseFloat(getValues("discountAmount")) || 0;

		const netAfterDiscount = totalAfterRef - finalDiscount;

		if (getValues("netAmount") !== netAfterDiscount) {
			setValue("netAmount", netAfterDiscount.toFixed(2), { shouldDirty: false });
		}
	};

	const billingRows = useWatch({ control, name: "billingCharges" });
	const discountPercentage = watch("discountPercentage");
	const discountAmount = watch("discountAmount"); // only if user edits manually
	const toReference = watch("toReference"); // only if user edits manually
	const totalAmount = watch("totalAmount");

	useEffect(() => {
		calculateAll();
	}, [billingRows, discountPercentage, discountAmount, toReference]);

	const fetchData = async () => {
		try {
			const response = await GetIpdBillChargeById(id); // Make sure login function returns token
			if (response.success) {
				Object.entries(response.data).forEach(([key, value]) => {
					// Handle main object dates
					if (key === "date" && value) {
						setValue(key, value.split("T")[0]);
						return;
					}

					// Handle billingChanges array
					if (key === "billingCharges" && Array.isArray(value)) {

						const formattedCharges = value.map((item) => ({
							...item,
							date: item.date ? item.date.split("T")[0] : "",
						}));

						replaceBillingCharges(formattedCharges);  // ✅ THIS IS IMPORTANT
						return;
					}

					// Default
					setValue(key, value);
				});

				setPatientDetails(response.data?.ipdEntryId || {});
				setValue('ipdEntryId', response.data?.ipdEntryId?._id || '');

			} else {
				// setError(response.message);
				showToast("error", response.message);
			}
		} catch (error) {
			// setError(error); // Handle login errors
			showToast("error", error);
		}
	};

	// Fetch existing data
	useEffect(
		() => {
			if (id) {
				fetchIpdEntries().then(() => {
					fetchBillingCharges().then(() => {
						fetchDoctors().then(() => {
							fetchData();
						});
					});
				});
			}
		},
		[id, setValue]
	);

	const onSubmit = async data => {
		try {
			dispatch({ type: "LOADER_START" });

			const response = await UpdateIpdBillCharge(id, data);
			if (response.success) {
				showToast("success", response.message);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error);
		} finally {
			dispatch({ type: "LOADER_STOP" });
		}
	};

	const formRef = useRef(null);
		  useKeyboardSubmit(handleSubmit, onSubmit);
		  useEnterNavigation(formRef);

	return (
		<Fragment>
			<Breadcrumb parent="IPD Charges" title="Edit IPD Charges Entry" />
			<div className="container-fluid">
				<div className="row">
					<div className="col-sm-12">
						<div className="card">
							<div className="card-header d-flex justify-content-between">
								<h5>Edit IPD Charges Entry</h5>
								<Link
									to={`${process.env
										.PUBLIC_URL}/ipd/transaction/ipd-bill-charges-entry/list`}
								>
									<button className="btn btn-primary" id="list">
										List IPD Charges Entry
									</button>
									<UncontrolledTooltip placement="top" target="list">
										List IPD Charges Entry
									</UncontrolledTooltip>
								</Link>
							</div>

							<form
								className="form theme-form"
								onSubmit={handleSubmit(onSubmit)}
							>
								<div className="card-body">
									<div className="row">
										<div className="col-6">
											<div className="form-group">
												<label>Room No</label>
												<select
													className="form-control"
													{...register("ipdEntryId", {
														onChange: (e) => {
															setPatientDetails(ipdEntries.find(ipd => ipd._id === e.target.value));
															setValue('advance', ipdEntries.find(ipd => ipd._id === e.target.value)?.advance || 0);
														}
													})}
												>
													<option value="">Select Ipd No</option>
													{ipdEntries.map(ipd =>
														<option key={ipd._id} value={ipd._id}>
															{ipd.roomId?.roomNo}
														</option>
													)}
												</select>
											</div>
										</div>
									</div>
									<div className="col-12">
										<h5 className="mt-3 mb-3 text-primary">
											Patient Details
										</h5>
									</div>
									<div className="row">
										<div className="col-6">
											<div className="form-group">
												<label>UHID No</label>
												<input
													className="form-control"
													placeholder="UHID No"
													value={patientDetails?.patientId?.uhidNo}
													readOnly
												/>
											</div>
										</div>
										<div className="col-6">
											<div className="form-group">
												<label>Admit Date</label>
												<input
													className="form-control"
													placeholder="Admit Date"
													value={patientDetails?.patientId?.date?.split("T")[0]}
													readOnly
												/>
											</div>
										</div>
										<div className="col-6">
											<div className="form-group">
												<label>Admit time</label>
												<input
													className="form-control"
													placeholder="Admit time"
													value={patientDetails?.patientId?.date?.split("T")[1].split(".")[0]}
													readOnly
												/>
											</div>
										</div>
										<div className="col-6">
											<div className="form-group">
												<label>Ipd No</label>
												<input
													className="form-control"
													placeholder="Ipd No"
													value={patientDetails?.patientId?.ipdNo}
													readOnly
												/>
											</div>
										</div>
										<div className="col-6">
											<div className="form-group">
												<label>Patient</label>
												<input
													className="form-control"
													placeholder="Patient Name"
													value={patientDetails?.patientId?.patientName}
													readOnly
												/>
											</div>
										</div>
										<div className="col-6">
											<div className="form-group">
												<label>guardian Name</label>
												<input
													className="form-control"
													placeholder="guardian Name"
													value={patientDetails?.patientId?.guardianName}
													readOnly
												/>
											</div>
										</div>
										<div className="col-6">
											<div className="form-group">
												<label>Age</label>
												<input
													className="form-control"
													placeholder="Patient Name"
													value={patientDetails?.patientId?.age}
													readOnly
												/>
											</div>
										</div>
										<div className="col-6">
											<div className="form-group">
												<label>Case</label>
												<input
													className="form-control"
													placeholder="Case"
													value={patientDetails?.patientId?.caseId?.name}
													readOnly
												/>
											</div>
										</div>
										<div className="col-6">
											<div className="form-group">
												<label>Consultant Doctor</label>
												<input
													className="form-control"
													placeholder="Consultant Doctor"
													value={patientDetails?.patientId?.consultantDoctor}
													readOnly
												/>
											</div>
										</div>
										<div className="col-6">
											<div className="form-group">
												<label>ReferenceDoctor</label>
												<input
													className="form-control"
													placeholder="ReferenceDoctor"
													value={patientDetails?.patientId?.refferenceDoctor}
													readOnly
												/>
											</div>
										</div>
										<div className="col-6">
											<div className="form-group">
												<label>Address</label>
												<input
													className="form-control"
													placeholder="Address"
													value={patientDetails?.patientId?.address}
													readOnly
												/>
											</div>
										</div>
									</div>
									<div className="col-12">
										<h5 className="mt-3 mb-3 text-primary">
											Billing Charges
										</h5>
									</div>
									<div className="row">
										<div className="col-12 my-3">
											<table className="table table-bordered">
												<thead>
													<tr>
														<th>Date</th>
														<th>Billing Charges</th>
														<th>Doctor Name</th>
														<th>Unit</th>
														<th>Rate</th>
														<th>Amount</th>
														<th>Disocunt</th>
														<th>V.No</th>
														<th>Action</th>
													</tr>
												</thead>

												<tbody>
													{billingChargesFields.map((field, index) => (
														<tr key={field.id}>
															<td>
																<input
																	className="form-control"
																	type="date"
																	{...register(`billingCharges.${index}.date`)}
																	placeholder="Date"
																/>
																{errors.billingCharges?.[index]?.date && (
																	<p className="text-danger">
																		{errors.billingCharges[index].date.message}
																	</p>
																)}
															</td>
															<td>
																<select
																	className="form-control"
																	{...register(`billingCharges.${index}.billingChargesId`)}
																>
																	<option value="">Select Charge Name</option>
																	{billingCharges.map((charge) => (
																		<option key={charge._id} value={charge._id}>
																			{charge.name}
																		</option>
																	))}
																</select>
																{errors.billingCharges?.[index]?.billingChargesId && (
																	<p className="text-danger">
																		{errors.billingCharges[index].billingChargesId.message}
																	</p>
																)}
															</td>

															<td>
																<select
																	className="form-control"
																	type="text"
																	{...register(`billingCharges.${index}.doctorId`)}
																	placeholder="Doctor Name"
																>
																	<option value="">Select Doctor</option>
																	{doctors.map((doctor) => (
																		<option key={doctor._id} value={doctor._id}>
																			{doctor.name}
																		</option>
																	))}
																</select>
																{errors.billingCharges?.[index]?.doctorId && (
																	<p className="text-danger">
																		{errors.billingCharges[index].doctorId.message}
																	</p>
																)}
															</td>

															<td>
																<input
																	className="form-control"
																	type="number"
																	{...register(`billingCharges.${index}.unit`)}
																	placeholder="Unit"
																/>
																{errors.billingCharges?.[index]?.unit && (
																	<p className="text-danger">
																		{errors.billingCharges[index].unit.message}
																	</p>
																)}
															</td>

															<td>
																<input
																	className="form-control"
																	type="number"
																	{...register(`billingCharges.${index}.rate`)}
																	placeholder="Rate"
																/>
																{errors.billingCharges?.[index]?.rate && (
																	<p className="text-danger">
																		{errors.billingCharges[index].rate.message}
																	</p>
																)}
															</td>

															<td>
																<input
																	className="form-control"
																	type="number"
																	{...register(`billingCharges.${index}.amount`)}
																	readOnly
																	placeholder="Amount"
																/>
																{errors.billingCharges?.[index]?.amount && (
																	<p className="text-danger">
																		{errors.billingCharges[index].amount.message}
																	</p>
																)}
															</td>

															<td>
																<select
																	className="form-control"
																	{...register(`billingCharges.${index}.discount`)}
																>
																	<option value="No">No</option>
																	<option value="Yes">Yes</option>
																</select>
																{errors.billingCharges?.[index]?.discount && (
																	<p className="text-danger">
																		{errors.billingCharges[index].discount.message}
																	</p>
																)}
															</td>

															<td>
																<input
																	className="form-control"
																	type="number"
																	{...register(`billingCharges.${index}.vNo`)}
																	placeholder="V No"
																/>
																{errors.billingCharges?.[index]?.vNo && (
																	<p className="text-danger">
																		{errors.billingCharges[index].vNo.message}
																	</p>
																)}
															</td>

															<td>
																<div className="d-flex">

																	<button
																		type="button"
																		className="btn btn-danger me-2"
																		onClick={() => billingChargesFields.length > 1 && removeBillingCharges(index)}
																	>
																		-
																	</button>
																	<button
																		type="button"
																		className="btn btn-primary"
																		onClick={() =>
																			appendBillingCharges({
																				date: "",
																				billingChargesId: "",
																				doctorId: "",
																				unit: "",
																				rate: "",
																				amount: "",
																				discount: "No",
																				vNo: "",
																			})
																		}
																	>
																		+
																	</button>
																</div>
															</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									</div>

									<div className="row">

										<div className="col-6">
											<div className="form-group">
												<label>Total Amount</label>
												<input
													type="number"
													className="form-control"
													{...register("totalAmount")}
													placeholder="total Amount"
													readOnly
												/>
												{errors.totalAmount &&
													<p className="text-danger">
														{errors.totalAmount.message}
													</p>}
											</div>
										</div>
										<div className="col-6">
											<div className="form-group">
												<label>Advance</label>
												<input
													type="number"
													className="form-control"
													{...register("advance")}
													placeholder="Advance"
													readOnly
												/>
												{errors.advance &&
													<p className="text-danger">
														{errors.advance.message}
													</p>}
											</div>
										</div>
										<div className="col-6">
											<div className="form-group">
												<label>To Reference</label>
												<input
													type="number"
													className="form-control"
													{...register("toReference")}
													placeholder="To Reference"
												/>
												{errors.toReference &&
													<p className="text-danger">
														{errors.toReference.message}
													</p>}
											</div>
										</div>

										<div className="col-6">
											<div className="form-group">
												<label>Discount Percentage</label>
												<input
													type="number"
													step="0.01"
													className="form-control"
													{...register("discountPercentage", {
														onChange: (e) => {
															if (e.target.value > 100) {
																e.target.value = 100;
															}
															const discountAmount = (e.target.value * totalAmount / 100).toFixed(2);
															setValue("discountAmount", discountAmount);
														}
													})}
													placeholder="Discount Percentage"
												/>
												{errors.discountPercentage &&
													<p className="text-danger">
														{errors.discountPercentage.message}
													</p>}
											</div>
										</div>

										<div className="col-6">
											<div className="form-group">
												<label>Discount Amount</label>
												<input
													type="number"
													step="0.01"
													className="form-control"
													{...register("discountAmount", {
														onChange: (e) => {
															const discountPercentage = (e.target.value * 100 / totalAmount).toFixed(2);
															setValue("discountPercentage", discountPercentage);
														}
													})}
													placeholder="Discount Amount"
												/>
												{errors.discountAmount &&
													<p className="text-danger">
														{errors.discountAmount.message}
													</p>}
											</div>
										</div>

										<div className="col-6">
											<div className="form-group">
												<label>Net Amount</label>
												<input
													type="number"
													className="form-control"
													{...register("netAmount")}
													placeholder="Net Amount"
													readOnly
												/>
												{errors.netAmount &&
													<p className="text-danger">
														{errors.netAmount.message}
													</p>}
											</div>
										</div>
										<div className="col-6">
											<div className="form-group">
												<label>Balance</label>
												<input
													type="number"
													className="form-control"
													{...register("balance")}
													placeholder="Balance"
													readOnly
												/>
												{errors.balance &&
													<p className="text-danger">
														{errors.balance.message}
													</p>}
											</div>
										</div>

										<div className="col-6">
											<div className="form-group">
												<label>Payment Mode</label>
												<select
													className="form-control"
													{...register("paymentMode")}
													placeholder="Payment Mode"
												>
													<option value="">Select Payment Mode</option>
													<option value="Cash">Cash</option>
													<option value="Online">Online</option>
												</select>
												{errors.paymentMode &&
													<p className="text-danger">
														{errors.paymentMode.message}
													</p>}
											</div>
										</div>
										<div className="col-12">
											<div className="form-group">
												<label>Remark</label>
												<textarea
													className="form-control"
													{...register("remark")}
													placeholder="Remark"
												/>
												{errors.remark &&
													<p className="text-danger">
														{errors.remark.message}
													</p>}
											</div>
										</div>
									</div>
								</div>

								<div className="card-footer text-end">
									<button className="btn btn-primary me-2" type="submit">
										Submit
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

export default EditIpdBillChargesEntry;
